import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ArrowBack } from '@material-ui/icons';
import { HomeVisitRecord, HomeVisitElement } from '../../../constants/Records';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    actionCell: {
      width: 120,
    },
    emptyMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    chipContainer: {
      display: 'flex',
      gap: theme.spacing(0.5),
      flexWrap: 'wrap',
    },
    chipProblem: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    chipNormal: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
  }),
);

// ADL/IADL/居宅内動作のフィールド一覧
const ADL_FIELDS = ['adl_feed', 'adl_ur', 'adl_bath', 'adl_dress', 'adl_cosme', 'adl_transfer'];
const IADL_FIELDS = ['iadl_indoor', 'iadl_outdoor', 'iadl_lift', 'iadl_cook', 'iadl_wash', 'iadl_clean'];
const BEHAVIOR_FIELDS = ['behaivior_arise', 'behaivior_sitting', 'behaivior_standup', 'behaivior_upright'];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: HomeVisitRecord[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, sequence: number) => void;
  onResetFlags: () => void;
}

const HomeVisitRecordList: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  executing,
  loaded,
  deleted,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<HomeVisitRecord | null>(null);

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records`);
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/homevisit/create`);
  };

  const handleEdit = (record: HomeVisitRecord) => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/homevisit/${record.sequence}/edit`);
  };

  const handleDeleteClick = (record: HomeVisitRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord && uniqueId) {
      onDelete(uniqueId, selectedRecord.sequence);
    }
  };

  // 訪問日時をフォーマット
  const formatVisitedDay = (visitedDay?: string) => {
    if (!visitedDay) return '-';
    // "yyyy-MM-dd HH:mm-HH:mm" 形式から日付部分を抽出
    const parts = visitedDay.split(' ');
    return parts[0] || visitedDay;
  };

  // 問題数をカウント
  const countProblems = (record: HomeVisitRecord) => {
    let adlProblems = 0;
    let iadlProblems = 0;
    let behaviorProblems = 0;

    ADL_FIELDS.forEach(field => {
      const element = (record as any)[field] as HomeVisitElement | undefined;
      if (element?.is_problem) adlProblems++;
    });

    IADL_FIELDS.forEach(field => {
      const element = (record as any)[field] as HomeVisitElement | undefined;
      if (element?.is_problem) iadlProblems++;
    });

    BEHAVIOR_FIELDS.forEach(field => {
      const element = (record as any)[field] as HomeVisitElement | undefined;
      if (element?.is_problem) behaviorProblems++;
    });

    return { adlProblems, iadlProblems, behaviorProblems };
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 訪問記録
      </Typography>

      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">訪問記録一覧</Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      {executing && !loaded ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : records.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>訪問記録はありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>訪問日</TableCell>
                <TableCell>スタッフ</TableCell>
                <TableCell>問題項目</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => {
                const problems = countProblems(record);
                const totalProblems = problems.adlProblems + problems.iadlProblems + problems.behaviorProblems;
                return (
                  <TableRow key={record.sequence}>
                    <TableCell>{record.sequence}</TableCell>
                    <TableCell>{formatVisitedDay(record.visited_day)}</TableCell>
                    <TableCell>{record.staff || '-'}</TableCell>
                    <TableCell>
                      <div className={classes.chipContainer}>
                        {totalProblems === 0 ? (
                          <Chip label="問題なし" size="small" className={classes.chipNormal} />
                        ) : (
                          <>
                            {problems.adlProblems > 0 && (
                              <Chip
                                label={`ADL: ${problems.adlProblems}`}
                                size="small"
                                className={classes.chipProblem}
                              />
                            )}
                            {problems.iadlProblems > 0 && (
                              <Chip
                                label={`IADL: ${problems.iadlProblems}`}
                                size="small"
                                className={classes.chipProblem}
                              />
                            )}
                            {problems.behaviorProblems > 0 && (
                              <Chip
                                label={`動作: ${problems.behaviorProblems}`}
                                size="small"
                                className={classes.chipProblem}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleEdit(record)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(record)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="訪問記録の削除"
        content={`訪問記録 No.${selectedRecord?.sequence} を削除しますか？`}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default HomeVisitRecordList;
